import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Shield, Cpu, Globe, Lock, Activity, Users, Database } from 'lucide-react';
import { Header } from '../components/ToxicDetector/Header';
import { Footer } from '../components/ToxicDetector/Footer';
import { MetaTags } from '../components/SEO/MetaTags';
export function AboutPage() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  return <>
      <MetaTags title="About ToxicDetector | Decoding the Invisible" description="We don't just read messages; we map the architecture of manipulation. Learn about our mission, forensic engine, and leadership." keywords="toxic detector mission, forensic AI, relationship analysis, Chukwuka Paulgreen Agodi, behavioral tech" canonical="https://toxicdetector.com/about" data-id="element-3475" />

      <div className="flex flex-col w-full min-h-screen bg-[#050505] text-white selection:bg-[#FF0000] selection:text-white" data-id="element-3476">
        <Header data-id="element-3477" />

        <main className="flex-grow relative overflow-hidden" data-id="element-3478">
          {/* Background Grid */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] opacity-20 pointer-events-none" data-id="element-3479" />

          <div className="max-w-6xl mx-auto px-6 py-20 relative z-10" data-id="element-3480">
            {/* Hero Section */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={containerVariants} className="mb-32 text-center md:text-left" data-id="element-3481">
              <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF0000]/30 bg-[#FF0000]/5 text-[#FF0000] text-xs font-mono uppercase tracking-widest mb-6" data-id="element-3482">
                <span className="w-2 h-2 rounded-full bg-[#FF0000] animate-pulse" data-id="element-3483" />
                System Status: Operational
              </motion.div>

              <motion.h1 variants={itemVariants} className="text-5xl md:text-7xl font-black tracking-tighter uppercase mb-6 leading-none" data-id="element-3484">
                Decoding the{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-500" data-id="element-3485">
                  Invisible
                </span>
                .
              </motion.h1>

              <motion.p variants={itemVariants} className="text-xl md:text-2xl text-gray-400 font-mono max-w-3xl border-l-2 border-[#FF0000] pl-6 ml-1" data-id="element-3486">
                We don't just read messages; we map the architecture of
                manipulation.
              </motion.p>
            </motion.section>

            {/* Mission & Methodology Grid */}
            <section className="grid md:grid-cols-2 gap-12 mb-32" data-id="element-3487">
              {/* Mission */}
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="relative group" data-id="element-3488">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-[#FF0000]/20 to-transparent opacity-0 group-hover:opacity-100 transition duration-500 blur rounded-lg" data-id="element-3489" />
                <div className="relative h-full bg-[#0A0A0A] border border-gray-800 p-8 rounded-lg" data-id="element-3490">
                  <div className="flex items-center gap-3 mb-6" data-id="element-3491">
                    <Activity className="text-[#FF0000]" size={24} data-id="element-3492" />
                    <h2 className="text-xl font-bold uppercase tracking-widest" data-id="element-3493">
                      The Mission
                    </h2>
                  </div>
                  <p className="text-gray-400 leading-relaxed mb-6" data-id="element-3494">
                    ToxicDetector was born from a singular realization: The
                    human nervous system is no match for modern psychological
                    warfare. While general AI is programmed to be "polite," our
                    Forensic Engine is programmed for the truth.
                  </p>
                  <p className="text-white font-medium border-t border-gray-800 pt-6" data-id="element-3495">
                    We provide the objective data needed to break the cycle of
                    cognitive dissonance.
                  </p>
                </div>
              </motion.div>

              {/* Methodology */}
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="relative group" data-id="element-3496">
                <div className="absolute -inset-0.5 bg-gradient-to-l from-[#FF0000]/20 to-transparent opacity-0 group-hover:opacity-100 transition duration-500 blur rounded-lg" data-id="element-3497" />
                <div className="relative h-full bg-[#0A0A0A] border border-gray-800 p-8 rounded-lg" data-id="element-3498">
                  <div className="flex items-center gap-3 mb-6" data-id="element-3499">
                    <Cpu className="text-[#FF0000]" size={24} data-id="element-3500" />
                    <h2 className="text-xl font-bold uppercase tracking-widest" data-id="element-3501">
                      The Methodology
                    </h2>
                  </div>
                  <p className="text-gray-400 leading-relaxed mb-6" data-id="element-3502">
                    Our proprietary{' '}
                    <span className="text-[#FF0000] font-mono" data-id="element-3503">
                      V.2.4.0 Forensic Engine
                    </span>{' '}
                    was developed at the intersection of Computational
                    Linguistics and Behavioral Psychology.
                  </p>
                  <p className="text-gray-400 leading-relaxed" data-id="element-3504">
                    By analyzing thousands of data points—from DARVO patterns to
                    intermittent reinforcement cycles—we provide a "sanity
                    check" backed by data, not just intuition.
                  </p>
                </div>
              </motion.div>
            </section>

            {/* Leadership Section */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={containerVariants} className="mb-32" data-id="element-3505">
              <div className="flex items-center gap-4 mb-12" data-id="element-3506">
                <div className="h-px flex-grow bg-gray-800" data-id="element-3507" />
                <h2 className="text-2xl font-black uppercase tracking-widest text-center" data-id="element-3508">
                  The Architecture
                </h2>
                <div className="h-px flex-grow bg-gray-800" data-id="element-3509" />
              </div>

              <div className="grid lg:grid-cols-3 gap-8" data-id="element-3510">
                {/* Founder Card */}
                <motion.div variants={itemVariants} className="lg:col-span-2 bg-[#0A0A0A] border border-gray-800 rounded-lg p-8 relative overflow-hidden group" data-id="element-3511">
                  <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-3512">
                    <Shield size={120} data-id="element-3513" />
                  </div>
                  <div className="relative z-10" data-id="element-3514">
                    <div className="inline-block px-3 py-1 bg-[#FF0000] text-white text-xs font-bold uppercase tracking-wider mb-4" data-id="element-3515">
                      Leadership
                    </div>
                    <h3 className="text-3xl font-bold mb-2" data-id="element-3516">
                      Chukwuka Paulgreen Agodi
                    </h3>
                    <p className="text-[#FF0000] font-mono text-sm uppercase tracking-widest mb-6" data-id="element-3517">
                      Founder & Chief Architect
                    </p>
                    <p className="text-gray-400 leading-relaxed max-w-2xl" data-id="element-3518">
                      ToxicDetector was created by Nigerian AI innovator
                      Chukwuka Paulgreen Agodi, who focuses on combining
                      behavioral psychology and artificial intelligence to
                      detect manipulation patterns in digital conversations. His
                      vision drives the core philosophy of the platform: making
                      the invisible mechanics of manipulation visible to
                      everyone.
                    </p>
                  </div>
                </motion.div>

                {/* Quantitative Team Card */}
                <motion.div variants={itemVariants} className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-8 flex flex-col justify-center relative overflow-hidden" data-id="element-3519">
                  <div className="absolute bottom-0 right-0 w-24 h-24 bg-[#FF0000]/5 rounded-full blur-2xl" data-id="element-3520" />
                  <Users className="text-[#FF0000] mb-4" size={32} data-id="element-3521" />
                  <h3 className="text-xl font-bold mb-2 uppercase tracking-wide" data-id="element-3522">
                    The Quantitative Team
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed" data-id="element-3523">
                    Comprising experts in AES-256 encryption and NLP (Natural
                    Language Processing), our team ensures your data remains
                    anonymous while your results remain surgical.
                  </p>
                </motion.div>
              </div>
            </motion.section>

            {/* Metrics Section */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={containerVariants} className="border-t border-gray-800 pt-20" data-id="element-3524">
              <div className="grid md:grid-cols-3 gap-8" data-id="element-3525">
                {/* Valuation */}
                <motion.div variants={itemVariants} className="text-center md:text-left" data-id="element-3526">
                  <div className="flex items-center justify-center md:justify-start gap-2 mb-2 text-gray-500 text-xs font-mono uppercase tracking-widest" data-id="element-3527">
                    <Activity size={14} data-id="element-3528" />
                    <span data-id="element-3529">Ecosystem Valuation</span>
                  </div>
                  <div className="text-4xl font-black text-white mb-1" data-id="element-3530">
                    $1.5M USD
                  </div>
                  <div className="text-[#FF0000] text-sm font-bold uppercase" data-id="element-3531">
                    Seed Stage
                  </div>
                </motion.div>

                {/* Data Integrity */}
                <motion.div variants={itemVariants} className="text-center md:text-left" data-id="element-3532">
                  <div className="flex items-center justify-center md:justify-start gap-2 mb-2 text-gray-500 text-xs font-mono uppercase tracking-widest" data-id="element-3533">
                    <Lock size={14} data-id="element-3534" />
                    <span data-id="element-3535">Data Integrity</span>
                  </div>
                  <div className="text-4xl font-black text-white mb-1" data-id="element-3536">
                    Zero-Knowledge
                  </div>
                  <div className="text-gray-400 text-sm" data-id="element-3537">Architecture</div>
                </motion.div>

                {/* Global Reach */}
                <motion.div variants={itemVariants} className="text-center md:text-left" data-id="element-3538">
                  <div className="flex items-center justify-center md:justify-start gap-2 mb-2 text-gray-500 text-xs font-mono uppercase tracking-widest" data-id="element-3539">
                    <Globe size={14} data-id="element-3540" />
                    <span data-id="element-3541">Global Reach</span>
                  </div>
                  <div className="text-4xl font-black text-white mb-1" data-id="element-3542">
                    500+ Scans
                  </div>
                  <div className="text-gray-400 text-sm" data-id="element-3543">
                    Hourly across 12 countries
                  </div>
                </motion.div>
              </div>
            </motion.section>

            {/* Final CTA — back to scanner */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={containerVariants} className="mt-32 mb-16" data-id="element-3544">
              <motion.div variants={itemVariants} className="relative bg-[#0a0a0a] border border-gray-800 p-10 md:p-14 overflow-hidden" data-id="element-3545">
                {/* Corner brackets */}
                <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#FF0000]" data-id="element-3546" />
                <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#FF0000]" data-id="element-3547" />
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#FF0000]" data-id="element-3548" />
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#FF0000]" data-id="element-3549" />

                {/* Subtle scan line */}
                <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#FF0000]/60 to-transparent" data-id="element-3550" />

                <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8" data-id="element-3551">
                  <div className="flex-1" data-id="element-3552">
                    <div className="inline-flex items-center gap-2 mb-4 text-[#FF0000] text-xs font-mono uppercase tracking-widest" data-id="element-3553">
                      <span className="w-2 h-2 rounded-full bg-[#FF0000] animate-pulse" data-id="element-3554" />
                      Ready to Decode
                    </div>
                    <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight uppercase mb-3 leading-tight" data-id="element-3555">
                      Now run it on a real conversation.
                    </h2>
                    <p className="text-gray-400 font-mono text-sm md:text-base max-w-xl" data-id="element-3556">
                      You've seen the architecture. Drop in a chat and watch the
                      patterns surface in seconds.
                    </p>
                  </div>

                  <a href="/upload" className="group flex-shrink-0 inline-flex items-center justify-center gap-3 bg-[#FF0000] text-white px-8 py-4 font-black uppercase tracking-widest text-sm shadow-[0_0_25px_rgba(255,0,0,0.35)] hover:bg-red-600 hover:shadow-[0_0_35px_rgba(255,0,0,0.55)] transition-all" data-id="element-3557">
                    <Activity className="w-5 h-5" data-id="element-3558" />
                    <span data-id="element-3559">Run a Scan</span>
                    <span className="transition-transform group-hover:translate-x-1" data-id="element-3560">
                      →
                    </span>
                  </a>
                </div>
              </motion.div>
            </motion.section>

            {/* Corporate Attribution */}
            <div className="mt-12 text-center" data-id="element-3561">
              <span className="text-xs font-mono uppercase tracking-widest text-gray-500" data-id="element-3562">
                A product of ToxicDetector AI & Forensic Systems LTD.
              </span>
            </div>
          </div>
        </main>

        <Footer data-id="element-3563" />
      </div>
    </>;
}